import {
  pageOffset,
  type ShareworksNotReady,
  type ShareworksRow,
  type ShareworksWorklist,
} from '@myrewards/contracts';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { meQuery } from '../../api';
import {
  claimShareworks,
  enteredShareworks,
  releaseShareworks,
  SHAREWORKS_EXPORT_HREF,
  shareworksKeys,
  shareworksQuery,
  undoShareworks,
  type GrantRef,
} from '../../api/close';
import { queueKeys } from '../../api/ops';
import { settingQuery } from '../../api/settings';
import { SettingChange } from '../../ops/SettingChange';
import { BackLink, Notice, PersonChip, TickBox } from '../../ops/shared';
import { Hero, PageBody } from '../../shell';
import {
  Button,
  Card,
  clientPage,
  ConfirmDialog,
  cx,
  DataTable,
  EmptyState,
  FilterBar,
  formatCount,
  formatDate,
  formatMoney,
  formatShortDate,
  formatTimestamp,
  Icon,
  Pager,
  plural,
  SortByMenu,
  SortHead,
  StatusPill,
  tableCards,
  useCardRows,
  useClampedPage,
  useListTable,
  usePaging,
  useScrollRegion,
  useToast,
  type DataColumn,
} from '../../ui';
import { QUEUE_VIEW, TYPE_SHORT, WORK_HREF } from '../../ops/labels';
import { NoMatches } from '../../ops/queue/NoMatches';
import { closeProblemWords, minutesWords, notClaimedLines } from '../labels';
import {
  FILTERS,
  filterCount,
  filterFromParam,
  GRANT_WORDS,
  grantAmount,
  grantKey,
  notReadyAmount,
  priceBasis,
  rowState,
  rowsMeta,
  rowsToEnter,
  SHAREWORKS_DEFAULT_SORT,
  SHAREWORKS_DEFAULT_WORDS,
  SHAREWORKS_SORT_COLUMNS,
  shareworksFilters,
  shareworksTie,
  takenWords,
  vestingWords,
  visibleRows,
  waitingWords,
  workingSentence,
  type ShareworksFilter,
} from './presenter';
import o from '../../ops/Ops.module.css';
import s from '../Close.module.css';
import { ManageTabs } from '../../manage/ManageTabs';

function WhoIsWorking({
  list,
  me,
}: {
  list: ShareworksWorklist;
  me: { name: string; initials: string };
}): ReactElement {
  const words = workingSentence(list);
  const mine = list.rows.filter((r) => r.yours).length;
  return (
    <section className={s.working} aria-labelledby="working-title">
      <span className={s.workingIcon} aria-hidden="true">
        {list.working[0]?.person.initials ?? <Icon name="people" size={18} />}
      </span>
      <div className={s.workingText}>
        <h2 id="working-title" className={s.workingTitle}>
          {words.title}
        </h2>
        <p className={o.muted}>{words.detail}</p>
      </div>
      <PersonChip
        initials={me.initials}
        name="You"
        detail={mine === 0 ? 'No rows claimed' : plural(mine, 'row', 'rows')}
        tone="cyan"
      />
    </section>
  );
}

function Filters({
  list,
  active,
}: {
  list: ShareworksWorklist;
  active: ShareworksFilter;
}): ReactElement {
  return (
    <nav aria-label="Show rows" className={o.filters}>
      {FILTERS.map((f) => (
        <RouterLink
          key={f.id}
          to={f.id === 'all' ? WORK_HREF.shareworks : `${WORK_HREF.shareworks}?show=${f.id}`}
          className={o.filter}
          {...(active === f.id && { 'aria-current': 'page' as const })}
        >
          {f.label}
          <span className={o.filterCount}>
            <span className="visually-hidden">, </span>
            {formatCount(filterCount(list, f.id))}
          </span>
        </RouterLink>
      ))}
    </nav>
  );
}

/** Grants on their way (the board's "Not ready"): what each waits on before it can be entered. */
function NotReadyTable({ rows }: { rows: readonly ShareworksNotReady[] }): ReactElement {
  const columns: DataColumn<ShareworksNotReady>[] = [
    {
      id: 'employee',
      header: 'Employee',
      card: 'title',
      cell: (r) => (
        <>
          <span className={s.cellStrong}>{r.person.name}</span>
          {r.person.eeNumber !== null && <span className={o.sub}>EE# {r.person.eeNumber}</span>}
        </>
      ),
    },
    {
      id: 'award',
      header: 'Award',
      cell: (r) => (
        <Link href={`/awards/${r.award.id}`} className={cx(o.strongLink, s.nowrap)}>
          {r.award.displayId}
        </Link>
      ),
    },
    {
      id: 'grant',
      header: 'Grant type',
      cell: (r) => (
        <>
          <span className={s.cellStrong}>{GRANT_WORDS[r.grant]}</span>
          <span className={o.sub}>{TYPE_SHORT[r.type]}</span>
        </>
      ),
    },
    { id: 'quantity', header: 'Quantity', numeric: true, cell: notReadyAmount },
    {
      id: 'waiting',
      header: 'Waiting on',
      cell: (r) => <StatusPill size="sm" tone="waiting" label={waitingWords(r)} />,
    },
  ];
  return (
    <DataTable
      caption="Grants not ready yet"
      columns={columns}
      rows={rows}
      rowKey={(r) => grantKey(r)}
      minWidth={720}
      cards
      empty={{
        title: 'Nothing on its way',
        body: 'Grants waiting on an election, Payroll or the statement check show here until they’re ready to enter.',
        art: 'calendar',
      }}
    />
  );
}

/** A grant's own price, and when it isn't today's FMV, a word that it isn't (QA-041). */
function PriceBasis({
  row,
  today,
}: {
  row: ShareworksRow;
  today: Parameters<typeof priceBasis>[1];
}): ReactElement {
  const basis = priceBasis(row.fmv, today);
  return (
    <>
      <span className={s.cellStrong}>{basis.text}</span>
      {basis.older && <span className={o.sub}>Its award’s FMV, not today’s</span>}
    </>
  );
}

/**
 * "Price basis" (SA5): the FMV in use, a day-one fact that shows before any grant arrives
 * (DESIGN-SPEC §5). Each grant is entered at the FMV its award was worked out with.
 */
function PriceBasisCard(): ReactElement {
  const fmv = useQuery(settingQuery('values.fmvCents', 'system'));
  const words = (value: unknown): string =>
    typeof value === 'number' ? `${formatMoney(value)} per share` : 'Not set';
  return (
    <Card
      title="Price basis"
      compact
      help={{
        shows:
          'The FMV in use today. Each grant is entered at the FMV its award was worked out with, shown in its Price basis column.',
        canDo:
          'Read it here. It’s changed in Settings, and a grant already worked out keeps its FMV.',
      }}
    >
      {fmv.isError ? (
        <p className={o.muted}>The FMV didn’t load. Reload the page to try again.</p>
      ) : fmv.data === undefined ? (
        <p className={o.muted}>Loading…</p>
      ) : (
        <>
          <p className={s.rateValue}>{words(fmv.data.value)}</p>
          <p className={o.muted}>
            {fmv.data.effectiveFrom === null
              ? 'One FMV for every award'
              : `FMV from ${formatDate(fmv.data.effectiveFrom)} · one FMV for every award`}
          </p>
          <p className={o.muted}>
            <SettingChange
              view={fmv.data}
              title="FMV per share"
              subject="History · Values"
              words={words}
            />
          </p>
        </>
      )}
    </Card>
  );
}

/** SA5: the Shareworks worklist: claim rows, enter them in Shareworks, tick them off. */
export function ShareworksPage(): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const { data: list } = useSuspenseQuery(shareworksQuery);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params] = useSearchParams();
  const filter = filterFromParam(params.get('show'));
  const inView = useMemo(() => visibleRows(list, filter), [list, filter]);
  // APP-149: the grants sort by their headings and filter by grant and award type, over the whole
  // list before it's paged; All, Open and Mine stay as they are. The queue's views keep one saved
  // sort (B6).
  const barRef = useRef<HTMLDivElement>(null);
  const filters = useMemo(() => shareworksFilters(list.rows), [list.rows]);
  const table = useListTable({
    table: 'queue',
    rows: inView,
    columns: SHAREWORKS_SORT_COLUMNS,
    defaultSort: SHAREWORKS_DEFAULT_SORT,
    defaultWords: SHAREWORKS_DEFAULT_WORDS,
    filters,
    tieBreak: shareworksTie,
    barRef,
  });
  const allRows = table.rows;
  // APP-135: the whole worklist is on the screen; 50 rows show at a time. One page setting serves
  // the grants and the not-ready tables, since only one of them shows at once.
  const paging = usePaging('shareworks');
  const { anchorRef } = paging;
  const paged = clientPage(allRows, paging.page, paging.size);
  const rows = paged.rows;
  const notReadyPaged = clientPage(list.notReady, paging.page, paging.size);
  useClampedPage(paging, (filter === 'not_ready' ? notReadyPaged : paged).page.number);
  // The bulk actions keep their reach, every row on every page. Each label says so when a row it
  // reaches isn't on the page in view (under any filter), and only then.
  const onScreen = new Set(filter === 'not_ready' ? [] : rows.map(grantKey));
  const everyPage = (reach: readonly ShareworksRow[]): string =>
    reach.some((r) => !onScreen.has(grantKey(r))) ? ', every page' : '';
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(scrollRef, 'Grants to enter');
  const [toClaim, setToClaim] = useState<ReadonlySet<string>>(new Set());
  const [entered, setEntered] = useState<ReadonlySet<string>>(new Set());
  const [checkingOff, setCheckingOff] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'problem' | 'info'; text: string } | null>(null);
  const [notYours, setNotYours] = useState<ReadonlySet<string>>(new Set());

  const open = list.rows.filter((r) => rowState(r) === 'open');
  const selectedOpen = open.filter((r) => toClaim.has(r.lineId));
  const tickedMine = list.rows.filter(
    (r) => r.yours && r.entered === null && entered.has(grantKey(r)),
  );
  const c = tableCards(useCardRows());
  // The claim timeout under the table is a setting: who changed it last, with its History.
  const lapseSetting = useQuery(settingQuery('shareworks.claimTimeoutMinutes'));
  // Today's FMV, to tell a grant worked out under an earlier one (QA-041).
  const todaysFmv = useQuery(settingQuery('values.fmvCents', 'system')).data;
  const today =
    todaysFmv !== undefined && typeof todaysFmv.value === 'number'
      ? { cents: todaysFmv.value, effectiveFrom: todaysFmv.effectiveFrom }
      : null;

  const refreshed = async (next?: ShareworksWorklist): Promise<void> => {
    if (next !== undefined) queryClient.setQueryData(shareworksKeys.list, next);
    else await queryClient.invalidateQueries({ queryKey: shareworksKeys.list });
    await queryClient.invalidateQueries({ queryKey: queueKeys.all });
  };

  const claim = useMutation({
    mutationFn: (lineIds: readonly string[]) => claimShareworks(lineIds),
    onSuccess: async (result) => {
      setToClaim(new Set());
      const taken = takenWords(result, list.rows);
      setNotice(taken === null ? null : { tone: 'info', text: taken });
      // The answer lists only what was claimed: read the worklist again for who has what.
      await refreshed();
      if (result.claimed.length > 0)
        toast.show({
          tone: 'done',
          title: `${plural(result.claimed.length, 'row', 'rows')} claimed`,
          body: `They’re yours until you check them off or hand them back. A claim lapses after ${minutesWords(list.claimTimeoutMinutes)} with no activity.`,
        });
    },
    onError: async (error) => {
      setNotice({ tone: 'problem', text: closeProblemWords(error) });
      await refreshed();
    },
  });

  const release = useMutation({
    mutationFn: (lineIds: readonly string[]) => releaseShareworks(lineIds),
    onSuccess: async (next, lineIds) => {
      setEntered(
        (set) => new Set([...set].filter((key) => !lineIds.some((id) => key.startsWith(id)))),
      );
      setNotice(null);
      await refreshed(next);
      toast.show({ tone: 'done', title: `${plural(lineIds.length, 'row', 'rows')} handed back` });
    },
    onError: (error) => {
      setNotice({ tone: 'problem', text: closeProblemWords(error) });
    },
  });

  const checkOff = useMutation({
    mutationFn: (grants: readonly GrantRef[]) => enteredShareworks(grants),
    onSuccess: async (result) => {
      setEntered(new Set());
      setNotYours(new Set());
      setNotice(null);
      await refreshed(result.worklist);
      toast.show({
        tone: 'done',
        title: `${plural(result.entered.length, 'grant', 'grants')} checked off`,
        body:
          result.closed.length === 0
            ? 'Recorded as entered in Shareworks, with who and when. Each person closes once all their grants are entered.'
            : `Recorded as entered in Shareworks, with who and when. ${plural(result.closed.length, 'person has', 'people have')} every grant entered and ${result.closed.length === 1 ? 'closes' : 'close'}.`,
      });
    },
    onError: async (error) => {
      const lines = notClaimedLines(error);
      setNotYours(new Set(lines));
      setNotice({ tone: 'problem', text: closeProblemWords(error) });
      await refreshed();
    },
  });

  const undo = useMutation({
    mutationFn: (grant: GrantRef) => undoShareworks([grant]),
    onSuccess: async (next, grant) => {
      setNotice(null);
      await refreshed(next);
      const row = list.rows.find((r) => r.lineId === grant.lineId && r.grant === grant.grant);
      toast.show({
        tone: 'done',
        title: 'Check-off undone',
        body:
          row === undefined
            ? 'It’s back to enter. The check-off and its undoing are both on the award’s history.'
            : `${row.person.name}’s ${GRANT_WORDS[grant.grant].toLowerCase()} are back to enter. The check-off and its undoing are both on the award’s history.`,
      });
    },
    onError: async (error) => {
      setNotice({ tone: 'problem', text: closeProblemWords(error) });
      await refreshed();
    },
  });

  const toggle = (
    set: ReadonlySet<string>,
    update: (next: ReadonlySet<string>) => void,
    id: string,
    on: boolean,
  ): void => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    update(next);
  };

  /**
   * A heading that sorts the grants (D3); plain words when the list is empty or in card rows. A
   * function, not a component, so a sorted heading keeps its focus.
   */
  const heading = (
    id: string,
    words: string,
    opts: { className?: string; numeric?: boolean } = {},
  ): ReactElement =>
    inView.length === 0 ? (
      <th scope="col" className={opts.className} {...c.header}>
        {words}
      </th>
    ) : (
      <SortHead
        column={id}
        sort={table.sort}
        onSortChange={table.setSort}
        numeric={opts.numeric}
        plain={c.on}
        pad={7}
        className={opts.className}
        {...c.header}
      >
        {words}
      </SortHead>
    );

  const mine = list.rows.filter((r) => r.yours).length;
  const timeout = minutesWords(list.claimTimeoutMinutes);
  const emptyRows = list.rows.length === 0;
  // One designed empty state for the table: its one row under the header row on a wide screen,
  // and alone on a phone, never a card around a card (SA5 board).
  const emptyState = emptyRows ? (
    list.enteredToday.length > 0 ? (
      <EmptyState
        variant="caught-up"
        size="sm"
        headline="All entered for now"
        body="Every grant that was waiting is in Shareworks. New ones land here once Payroll has paid and the statement check is done, or straight after the letters for an award with options only."
      />
    ) : (
      <EmptyState
        variant="first-day"
        art="award"
        size="sm"
        headline="No grants to enter yet"
        body="Grants land here once Payroll has paid and the statement check is done, or straight after the letters for an award with options only. Claim rows, enter them in Shareworks, then tick them off."
      />
    )
  ) : inView.length > 0 ? (
    <NoMatches what="grants" onClear={table.clearFromEmpty} />
  ) : (
    <EmptyState
      variant="first-day"
      art="search"
      size="sm"
      headline={filter === 'mine' ? 'No rows claimed by you' : 'No open rows'}
      body={
        filter === 'mine'
          ? 'Claim open rows to work them; they show here until you check them off.'
          : 'Every row is claimed. Rows come back to open when they’re handed back or a claim lapses.'
      }
      action={{ label: 'Show all rows', href: WORK_HREF.shareworks }}
    />
  );

  return (
    <>
      <Hero
        eyebrow="Grants · Stock Admin"
        title="Shareworks worklist"
        sub="Every share and option grant to enter in Shareworks. Claim rows so nobody enters a grant twice, then check them off."
        stats={[
          {
            label: 'All sectors · grants to enter',
            value: formatCount(rowsToEnter(list).length),
            meta: rowsMeta(list),
          },
          {
            label: 'Shareworks · claimed by you',
            value: formatCount(mine),
            meta:
              list.working.length === 0
                ? 'Nobody else has rows'
                : list.working
                    .map((w) => `${w.person.name} has ${plural(w.rows, 'row', 'rows')}`)
                    .join(' · '),
          },
        ]}
        titleTestId="shareworks-title"
      />
      <ManageTabs current="shareworks" />
      <PageBody>
        <div className={o.stack}>
          <BackLink href={QUEUE_VIEW.queue.href}>Work queue</BackLink>
          <WhoIsWorking
            list={list}
            me={{ name: me.person.displayName, initials: me.person.initials }}
          />
          <Card
            title="Grants to enter"
            helpAnchor="shareworks-grants"
            help={{
              shows:
                'Every grant to enter, one row each: a person’s options, and the shares their election bought, the oldest grant date first. Who is on each person shows beside it.',
              canDo:
                'Sort by a heading or filter by grant and award type. Claim a person or a batch, enter each grant in Shareworks, then tick “Entered in Shareworks” and check them off. Hand rows back if you can’t finish them. Export the list as CSV.',
            }}
            aside={
              <div className={s.buttonRow}>
                {/* A System Admin sees the list and can export it, with nothing to claim (APP-134). */}
                {!list.processes ? null : selectedOpen.length > 0 ? (
                  <Button
                    size="sm"
                    isPending={claim.isPending}
                    onPress={() => {
                      claim.mutate(selectedOpen.map((r) => r.lineId));
                    }}
                  >
                    Claim selected{everyPage(selectedOpen)} · {formatCount(selectedOpen.length)}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    isDisabled={open.length === 0}
                    isPending={claim.isPending}
                    onPress={() => {
                      claim.mutate(open.map((r) => r.lineId));
                    }}
                  >
                    Claim all open rows{everyPage(open)} · {formatCount(open.length)}
                  </Button>
                )}
                {list.processes && (
                  <Button
                    size="sm"
                    variant="ghost"
                    isDisabled={tickedMine.length === 0}
                    onPress={() => {
                      setCheckingOff(true);
                    }}
                  >
                    Check off entered{everyPage(tickedMine)} · {formatCount(tickedMine.length)}
                  </Button>
                )}
                <a href={SHAREWORKS_EXPORT_HREF} className={s.exportLink} download>
                  <Icon name="download" size={16} />
                  Export CSV
                </a>
              </div>
            }
          >
            <Filters list={list} active={filter} />
            {filter !== 'not_ready' && filter !== 'entered' && inView.length > 0 && (
              <div ref={barRef}>
                <FilterBar label="Filter the grants" {...table.bar} />
              </div>
            )}
            {notice !== null && <Notice tone={notice.tone}>{notice.text}</Notice>}
            {filter === 'not_ready' ? (
              <>
                <section ref={anchorRef} tabIndex={-1}>
                  <NotReadyTable rows={notReadyPaged.rows} />
                </section>
                <Pager
                  label="Grants not ready yet"
                  page={notReadyPaged.page}
                  onPage={(n) => {
                    paging.setPage(n);
                  }}
                  onSize={(size) => {
                    paging.setSize(size, pageOffset(notReadyPaged.page));
                  }}
                />
              </>
            ) : filter === 'entered' ? (
              list.enteredToday.length === 0 ? (
                <EmptyState
                  variant="first-day"
                  art="calendar"
                  headline="Nothing entered today"
                  body="Rows you and the team check off today show here, with who entered each one and when."
                />
              ) : (
                <ul className={s.enteredList}>
                  {list.enteredToday.map((e) => (
                    <li key={`${grantKey(e)}:${e.enteredAt}`} className={s.enteredRow}>
                      <span className={s.cellStrong}>
                        {e.person.name} · {grantAmount(e)}
                      </span>
                      <span className={o.muted}>
                        {e.award.displayId} · entered by {e.enteredBy.name} ·{' '}
                        {formatTimestamp(e.enteredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            ) : c.on && rows.length === 0 ? (
              emptyState
            ) : (
              <>
                <section ref={anchorRef} tabIndex={-1}>
                  {/* Card rows have no headings to press: the Sort by menu takes their place. */}
                  {c.on && inView.length > 0 && (
                    <SortByMenu
                      columns={SHAREWORKS_SORT_COLUMNS}
                      rows={inView}
                      sort={table.sort}
                      onSortChange={table.setSort}
                    />
                  )}
                  <div ref={scrollRef} className={s.tableScroll} {...(c.on ? {} : region)}>
                    <table {...c.table(cx(s.table, s.wide))}>
                      <caption className="visually-hidden">Grants to enter</caption>
                      <thead {...c.head}>
                        <tr {...c.row}>
                          <th scope="col" {...c.header}>
                            <span className="visually-hidden">Select to claim</span>
                          </th>
                          {heading('employee', 'Employee')}
                          {heading('award', 'Award')}
                          {heading('grant', 'Grant type')}
                          {heading('quantity', 'Quantity', { className: s.num, numeric: true })}
                          <th scope="col" {...c.header}>
                            Price basis
                          </th>
                          {heading('grantDate', 'Grant date')}
                          <th scope="col" {...c.header}>
                            Vesting
                          </th>
                          {heading('claimed', 'Claimed by')}
                          <th scope="col" {...c.header}>
                            Entered in Shareworks
                          </th>
                        </tr>
                      </thead>
                      <tbody {...c.body}>
                        {rows.length === 0 && (
                          <tr className={s.emptyRow} {...c.row}>
                            <td colSpan={10} {...c.wide}>
                              {emptyState}
                            </td>
                          </tr>
                        )}
                        {rows.map((row) => {
                          const state = rowState(row);
                          const name = row.person.name;
                          const what = `${name}’s ${row.award.displayId} ${GRANT_WORDS[row.grant].toLowerCase()}`;
                          const key = grantKey(row);
                          return (
                            <tr
                              key={key}
                              className={cx(
                                state === 'yours' && s.mineRow,
                                notYours.has(row.lineId) && s.problemRow,
                              )}
                              {...c.row}
                            >
                              <td {...c.cell('Select to claim')}>
                                {state === 'open' && list.processes ? (
                                  <TickBox
                                    isSelected={toClaim.has(row.lineId)}
                                    onChange={(on) => {
                                      toggle(toClaim, setToClaim, row.lineId, on);
                                    }}
                                    label={`Select ${name}’s ${row.award.displayId} rows to claim`}
                                  >
                                    <span />
                                  </TickBox>
                                ) : c.on ? null : (
                                  <span className="visually-hidden">
                                    {state === 'yours' ? 'Yours' : 'Claimed'}
                                  </span>
                                )}
                              </td>
                              <td {...c.cell('Employee', 'title')}>
                                <span className={s.cellStrong}>{name}</span>
                                {row.person.eeNumber !== null && (
                                  <span className={o.sub}>EE# {row.person.eeNumber}</span>
                                )}
                              </td>
                              <td {...c.cell('Award')}>
                                <Link
                                  href={`/awards/${row.award.id}`}
                                  className={cx(o.strongLink, s.nowrap)}
                                >
                                  {row.award.displayId}
                                </Link>
                              </td>
                              <td {...c.cell('Grant type')}>
                                <span className={s.cellStrong}>{GRANT_WORDS[row.grant]}</span>
                                <span className={o.sub}>{TYPE_SHORT[row.type]}</span>
                              </td>
                              <td {...c.cell('Quantity', 'value', s.num)}>
                                {formatCount(row.quantity)}
                              </td>
                              <td {...c.cell('Price basis')}>
                                <PriceBasis row={row} today={today} />
                              </td>
                              <td {...c.cell('Grant date')}>
                                {row.grantDate === null
                                  ? 'Not set yet'
                                  : formatShortDate(row.grantDate)}
                              </td>
                              <td {...c.cell('Vesting')}>{vestingWords(row)}</td>
                              <td
                                {...c.cell(
                                  'Claimed by',
                                  state === 'open' && list.processes ? 'action' : 'value',
                                )}
                              >
                                {state === 'open' && !list.processes ? (
                                  <span className={o.muted}>Not claimed yet</span>
                                ) : state === 'open' ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onPress={() => {
                                      claim.mutate([row.lineId]);
                                    }}
                                    aria-label={`Claim ${name}’s ${row.award.displayId} rows`}
                                  >
                                    Claim
                                  </Button>
                                ) : state === 'yours' ? (
                                  <div className={s.claimCell}>
                                    <PersonChip
                                      initials={me.person.initials}
                                      name="You"
                                      tone="cyan"
                                    />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onPress={() => {
                                        release.mutate([row.lineId]);
                                      }}
                                      aria-label={`Hand back ${name}’s ${row.award.displayId} rows`}
                                    >
                                      Hand back
                                    </Button>
                                  </div>
                                ) : row.claimedBy === null ? null : (
                                  <PersonChip
                                    initials={row.claimedBy.initials}
                                    name={row.claimedBy.name}
                                    {...(row.claimedAt !== null && {
                                      detail: `Since ${formatTimestamp(row.claimedAt)}`,
                                    })}
                                  />
                                )}
                              </td>
                              <td
                                {...c.cell(
                                  'Entered in Shareworks',
                                  state === 'yours' ? 'action' : 'value',
                                )}
                              >
                                {row.entered !== null ? (
                                  <div className={s.claimCell}>
                                    <span className={o.sub}>
                                      Entered by {row.entered.by.name} ·{' '}
                                      {formatTimestamp(row.entered.at)}
                                    </span>
                                    {state === 'yours' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        // Once undone this button goes: no React Aria pending
                                        // announcement left pointing at it; the toast says it.
                                        aria-disabled={undo.isPending}
                                        aria-busy={undo.isPending}
                                        onPress={() => {
                                          if (!undo.isPending)
                                            undo.mutate({ lineId: row.lineId, grant: row.grant });
                                        }}
                                        aria-label={`Undo the check-off for ${what}`}
                                      >
                                        Undo
                                      </Button>
                                    )}
                                  </div>
                                ) : state === 'yours' ? (
                                  <TickBox
                                    isSelected={entered.has(key)}
                                    onChange={(on) => {
                                      toggle(entered, setEntered, key, on);
                                    }}
                                    label={`Entered in Shareworks: ${what}`}
                                  >
                                    <span className={o.sub}>Entered</span>
                                  </TickBox>
                                ) : (
                                  <span className={o.muted}>
                                    {state === 'open'
                                      ? list.processes
                                        ? 'Claim it first'
                                        : 'Not entered yet'
                                      : 'Theirs to enter'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
                <Pager
                  label="Grants to enter"
                  page={paged.page}
                  onPage={(n) => {
                    paging.setPage(n);
                  }}
                  onSize={(size) => {
                    paging.setSize(size, pageOffset(paged.page));
                  }}
                />
              </>
            )}
            <p className={s.lapse}>
              <Icon name="clock" size={18} />
              <span>
                <span className={o.strong}>A claim lapses after {timeout} with no activity.</span>{' '}
                Anyone can take a lapsed row.
                {lapseSetting.data !== undefined && (
                  <>
                    {' '}
                    <SettingChange
                      view={lapseSetting.data}
                      title="Shareworks claim timeout"
                      subject="History · Elections and letters"
                      words={(v) => (typeof v === 'number' ? minutesWords(v) : 'Not set')}
                    />
                  </>
                )}
              </span>
            </p>
          </Card>
          <div className={s.threeUp}>
            <Card
              title="Entered today"
              compact
              help={{
                shows:
                  'Rows checked off as entered in Shareworks today, by anyone on the team, newest first.',
                canDo: 'Nothing to do here: it’s the record of today’s entries, with who and when.',
              }}
            >
              {list.enteredToday.length === 0 ? (
                <EmptyState
                  variant="first-day"
                  art="calendar"
                  size="sm"
                  headline="Nothing entered today"
                  body="Each row checked off today shows here with who entered it and when."
                />
              ) : (
                <ul className={s.enteredList}>
                  {list.enteredToday.slice(0, 6).map((e) => (
                    <li key={`${grantKey(e)}:${e.enteredAt}`} className={s.enteredRow}>
                      <span className={s.cellStrong}>
                        {e.person.name} · {grantAmount(e)}
                      </span>
                      <span className={o.muted}>
                        {e.award.displayId} · {e.enteredBy.name} · {formatTimestamp(e.enteredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <PriceBasisCard />
            <Card
              title="When a row is ready"
              compact
              help={{
                shows: 'When each kind of grant reaches this list.',
                canDo: 'Open the statement check for shares still waiting on Payroll.',
              }}
            >
              <p className={o.muted}>
                A person’s grants come here once Payroll has paid and the share count matches the
                earning statement; an award with options only comes straight after its letters.
                Until then they’re under “Not ready”, with what each waits on.
              </p>
              <Link href={WORK_HREF.earning_statements} className={o.inlineLink}>
                Earning statements
              </Link>
            </Card>
            <Card
              title="Checking a row off"
              tone="dark"
              compact
              help={{
                shows: 'What ticking “Entered in Shareworks” records.',
                canDo: 'Tick the rows you entered, then check them off together.',
              }}
            >
              <p className={o.onDarkNote}>
                Ticking “Entered in Shareworks” records who and when, for each grant. When all of a
                person’s grants are entered, they close. A wrong tick can be undone while their
                other grant waits; both stay on the history.
              </p>
            </Card>
          </div>
        </div>
      </PageBody>
      <ConfirmDialog
        isOpen={checkingOff}
        onOpenChange={setCheckingOff}
        title={`Check off ${plural(tickedMine.length, 'grant', 'grants')}?`}
        intro="Each one is recorded as entered in Shareworks, with you and the time. A person closes once all their grants are entered."
        summary={tickedMine.map((r) => ({
          label: `${r.award.displayId} · ${r.person.name}`,
          value: grantAmount(r),
        }))}
        withoutReason
        confirmLabel="Check them off"
        onConfirm={async () => {
          await checkOff
            .mutateAsync(tickedMine.map((r) => ({ lineId: r.lineId, grant: r.grant })))
            .catch(() => undefined);
        }}
      />
    </>
  );
}
